// src/components/NotificationDetailModal.tsx
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Megaphone, Bell as BellIcon, Clock } from "lucide-react";
import { Notification } from "@/api/service/notifications";
import { cn } from "@/lib/utils";

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

// Detail view for a single notification. Announcements show a title + body;
// personal notifications fall back to the message. Body uses pre-line so
// admin-authored newlines (`→` bullets, paragraphs) survive rendering.
export default function NotificationDetailModal({
  notification,
  open,
  onClose,
}: {
  notification: Notification | null;
  open: boolean;
  onClose: () => void;
}) {
  const isAnnouncement = notification?.kind === "announcement";
  const title =
    notification?.title ||
    (isAnnouncement ? "Announcement" : "Notification");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/60 space-y-2">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center justify-center h-6 w-6 rounded-md shrink-0",
                isAnnouncement
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground"
              )}
              aria-hidden
            >
              {isAnnouncement ? (
                <Megaphone className="h-3.5 w-3.5" />
              ) : (
                <BellIcon className="h-3.5 w-3.5" />
              )}
            </span>
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {isAnnouncement ? "Announcement" : "Notification"}
            </span>
          </div>
          <DialogTitle className="text-lg font-semibold leading-snug pr-6">
            {title}
          </DialogTitle>
          {notification?.created_at && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Clock className="h-2.5 w-2.5" />
              <span>Posted {formatTimestamp(notification.created_at)}</span>
            </div>
          )}
        </DialogHeader>

        <div className="px-5 py-5 max-h-[60vh] overflow-y-auto">
          {notification ? (
            <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line break-words">
              {notification.message}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
